import {
  CalendarOptions,
  DatesSetArg,
  DayHeaderContentArg,
  EventMountArg,
  MoreLinkContentArg,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Item } from '../form/select-field/select-field.component';
import {
  EVENT_MIN_VISUAL_MINUTES,
  formatTime,
  getMoreLinkLabel,
  getScrollTimeString,
} from './calendar.utils';

/** A machinery slot boundary currently previewed under the cursor. */
export interface MachinerySlotPreview {
  eventId: string;
  start: Date;
  end: Date;
}

export interface CalendarToolbarFlags {
  addNewEventButton: boolean;
  changeViewButton: boolean;
  hasExtraHeaderTemplate: boolean;
  addPrestazioneButton: boolean;
  dateRangeSelection: boolean;
}

/**
 * Builds the FullCalendar `headerToolbar.end` button list and the matching
 * `customButtons` entries from the page's requested toolbar flags.
 */
export function buildToolbarButtons(flags: CalendarToolbarFlags): {
  endToolbarButtons: string[];
  customButtons: Record<string, object>;
} {
  const customButtons: Record<string, object> = {};
  const endToolbarButtons: string[] = [];

  if (flags.addNewEventButton) {
    endToolbarButtons.push('addButton');
    customButtons['addButton'] = {};
  }
  if (flags.changeViewButton) {
    endToolbarButtons.push('changeView');
    customButtons['changeView'] = {};
  }
  if (flags.hasExtraHeaderTemplate) {
    endToolbarButtons.push('extraHeader');
    customButtons['extraHeader'] = { hint: 'Controlli vista calendario' };
  }
  if (flags.addPrestazioneButton) {
    endToolbarButtons.push('addPrestazioneButton');
    customButtons['addPrestazioneButton'] = {};
  }
  if (flags.dateRangeSelection) {
    endToolbarButtons.push('dateRange');
    customButtons['dateRange'] = {};
  }

  return { endToolbarButtons, customButtons };
}

/**
 * Adds the "macchinario" label to the event's tooltip.
 */
function applyMacchinarioLabel(info: EventMountArg): void {
  const label = info.event.extendedProps?.['macchinarioLabel'] as
    { nome: string; fasciaOraria: string; durata?: string | null } | undefined;
  if (!label) return;

  info.el
    .querySelectorAll(':scope > .disponibilita-macchinario-label')
    .forEach(el => el.remove());

  const labelEl = document.createElement('div');
  labelEl.className = 'disponibilita-macchinario-label';

  const nomeEl = document.createElement('div');
  nomeEl.className = 'disponibilita-macchinario-label-nome';
  nomeEl.textContent = label.nome;
  labelEl.appendChild(nomeEl);

  const fasciaEl = document.createElement('div');
  fasciaEl.textContent = label.fasciaOraria;
  labelEl.appendChild(fasciaEl);

  if (label.durata) {
    const durataEl = document.createElement('div');
    durataEl.textContent = label.durata;
    labelEl.appendChild(durataEl);
  }

  info.el.appendChild(labelEl);
}

/**
 * Tracks the cursor over a machinery availability block (time-grid views
 * only), snapping its vertical position to the machinery's slot grid and
 * rendering a ghost preview of the exact slot a click would book.
 */
function attachMachineryHoverPreview(
  info: EventMountArg,
  config: {
    snapToSlot: (date: Date, macchinario: Item) => Date | null;
    isDateAllowed: (date: Date) => boolean;
    hasSlotConflict: (
      idMacchinario: unknown,
      start: Date,
      end: Date
    ) => boolean;
    isPreviewEnabled: () => boolean;
    onHover: (preview: MachinerySlotPreview | null) => void;
  }
): void {
  if (!info.view.type.startsWith('timeGrid')) return;

  const macchinario = info.event.extendedProps?.['macchinario'] as
    Item | undefined;
  const eventStart = info.event.start;
  const eventEnd = info.event.end;
  if (!macchinario?.durataSlotMacchinario || !eventStart || !eventEnd) return;

  const previewEl = document.createElement('div');
  previewEl.className = 'machinery-slot-preview';
  info.el.appendChild(previewEl);

  const spanMs = eventEnd.getTime() - eventStart.getTime();

  const hide = (): void => {
    previewEl.style.display = 'none';
    config.onHover(null);
  };

  const handleMove = (ev: MouseEvent): void => {
    if (!config.isPreviewEnabled()) {
      hide();
      return;
    }

    const rect = info.el.getBoundingClientRect();
    const fraction = Math.min(
      1,
      Math.max(0, (ev.clientY - rect.top) / rect.height)
    );
    const snappedStart = config.snapToSlot(
      new Date(eventStart.getTime() + fraction * spanMs),
      macchinario
    );
    if (!snappedStart || !config.isDateAllowed(snappedStart)) {
      hide();
      return;
    }

    const snappedEnd = new Date(
      snappedStart.getTime() + macchinario.durataSlotMacchinario * 60000
    );
    if (config.hasSlotConflict(macchinario.id, snappedStart, snappedEnd)) {
      hide();
      return;
    }

    previewEl.style.display = 'block';
    previewEl.style.top = `${((snappedStart.getTime() - eventStart.getTime()) / spanMs) * 100}%`;
    previewEl.style.height = `${((snappedEnd.getTime() - snappedStart.getTime()) / spanMs) * 100}%`;
    previewEl.textContent = `${formatTime(snappedStart)} - ${formatTime(snappedEnd)}`;
    config.onHover({
      eventId: info.event.id,
      start: snappedStart,
      end: snappedEnd,
    });
  };

  info.el.addEventListener('mousemove', handleMove);
  info.el.addEventListener('mouseleave', hide);
}

/**
 * Attaches hover elevation and min-height to events (DEFAULT BEHAVIOR).
 */
function attachEventHoverElevation(
  info: EventMountArg,
  onHoverChange: (eventId: string | null) => void
): void {
  if (info.view.type !== 'timeGridWeek') return;

  const target =
    info.el.closest<HTMLElement>('.fc-timegrid-event-harness') ?? info.el;
  const isBackgroundEvent = target === info.el;

  let boundsCheck: ((ev: MouseEvent) => void) | null = null;
  const stopBoundsCheck = (): void => {
    if (!boundsCheck) return;
    document.removeEventListener('mousemove', boundsCheck);
    boundsCheck = null;
  };

  const handleLeave = (): void => {
    target.style.zIndex = '';
    target.classList.remove('fc-event-hovered');
    onHoverChange(null);
    stopBoundsCheck();
  };
  const handleEnter = (): void => {
    target.style.zIndex = '9999';
    target.classList.add('fc-event-hovered');
    onHoverChange(info.event.id);

    if (isBackgroundEvent) {
      const rect = target.getBoundingClientRect();
      const colFrame = target.closest<HTMLElement>('.fc-timegrid-col-frame');
      const foregroundHarnesses = colFrame
        ? Array.from(
            colFrame.querySelectorAll<HTMLElement>('.fc-timegrid-event-harness')
          )
        : [];

      boundsCheck = (ev: MouseEvent): void => {
        const outsideOwnBounds =
          ev.clientX < rect.left ||
          ev.clientX > rect.right ||
          ev.clientY < rect.top ||
          ev.clientY > rect.bottom;
        const overForegroundEvent = foregroundHarnesses.some(harness => {
          const r = harness.getBoundingClientRect();
          return (
            ev.clientX >= r.left &&
            ev.clientX <= r.right &&
            ev.clientY >= r.top &&
            ev.clientY <= r.bottom
          );
        });
        if (outsideOwnBounds || overForegroundEvent) {
          handleLeave();
        }
      };
      document.addEventListener('mousemove', boundsCheck);
    }
  };

  info.el.addEventListener('mouseenter', handleEnter);
  info.el.addEventListener('mouseleave', handleLeave);
}

/**
 * Builds the base FullCalendar options object.
 */
export function buildBaseCalendarOptions(config: {
  translateLang: string;
  isDateAllowed: (date: Date) => boolean;
  hasBackgroundEventAt: (date: Date) => boolean;
  isHoliday: (date: Date) => boolean;
  endToolbarButtons: string[];
  customButtons: Record<string, object>;
  onViewReady: () => void;
  renderCustomDayHeader: (props: DayHeaderContentArg) => { html: string };
  onDatesSet: (info: DatesSetArg) => void;
  snapMachinerySlot: (date: Date, macchinario: Item) => Date | null;
  hasMachinerySlotConflict: (
    idMacchinario: unknown,
    start: Date,
    end: Date
  ) => boolean;
  isMachineryPreviewEnabled: () => boolean;
  onMachineryHover: (preview: MachinerySlotPreview | null) => void;
  onEventHoverChange: (eventId: string | null) => void;
}): Partial<CalendarOptions> {
  return {
    plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin],
    initialView: 'timeGridWeek',
    height: 'auto',
    timeZone: 'local',
    slotEventOverlap: false,
    eventMaxStack: 2,
    moreLinkClick: 'popover',
    moreLinkContent: (arg: MoreLinkContentArg) =>
      `+${arg.num} ${getMoreLinkLabel(config.translateLang)}`,
    dayPopoverFormat: { weekday: 'long', day: 'numeric', month: 'long' },
    views: {
      dayGridMonth: {
        dayMaxEvents: 1,
      },
      timeGridDay: {
        eventMaxStack: 2,
      },
      threeDay: {
        type: 'timeGrid',
        duration: { days: 3 },
        buttonText: '3 giorni',
      },
      timeGridWeek: {
        eventMinHeight: (EVENT_MIN_VISUAL_MINUTES * (2.7 * 16)) / 30,
      },
    },
    locale: config.translateLang,
    firstDay: 1,
    titleFormat: { year: 'numeric', month: 'long' },
    buttonText: {
      today: 'Oggi',
      month: 'Mese',
      week: 'Settimana',
      day: 'Giorno',
    },
    buttonHints: {
      prev: 'Periodo precedente',
      next: 'Periodo successivo',
      today: 'Vai a oggi',
    },
    dayHeaderFormat: { weekday: 'short', day: 'numeric', omitCommas: true },
    slotLabelFormat: {
      hour: '2-digit',
      omitZeroMinute: true,
      hour12: false,
    },
    eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
    nowIndicator: true,
    now: () => new Date(),
    selectAllow: selectInfo =>
      config.isDateAllowed(selectInfo.start) ||
      config.hasBackgroundEventAt(selectInfo.start),
    eventAllow: dropInfo => config.isDateAllowed(dropInfo.start),
    eventDisplay: 'block',
    eventOrder: '-hasMatchingPrestazione,start,-duration,allDay,title',
    dayMaxEvents: true,
    slotDuration: '00:30:00',
    snapDuration: '00:15:00',
    slotLabelInterval: '01:00',
    scrollTime: getScrollTimeString(),
    allDaySlot: false,
    allDayText: 'Tutto il giorno',
    headerToolbar: {
      start: 'today prev,next',
      center: 'title',
      end: config.endToolbarButtons.join(' '),
    },
    customButtons: config.customButtons,
    viewDidMount: () => config.onViewReady(),
    dayHeaderContent: props => config.renderCustomDayHeader(props),
    datesSet: (info: DatesSetArg) => config.onDatesSet(info),
    dayCellClassNames: arg =>
      config.isHoliday(arg.date) ? ['is-holiday'] : [],
    eventDidMount: info => {
      applyMacchinarioLabel(info);
      attachMachineryHoverPreview(info, {
        snapToSlot: config.snapMachinerySlot,
        isDateAllowed: config.isDateAllowed,
        hasSlotConflict: config.hasMachinerySlotConflict,
        isPreviewEnabled: config.isMachineryPreviewEnabled,
        onHover: config.onMachineryHover,
      });
      attachEventHoverElevation(info, config.onEventHoverChange);
    },
  };
}
